import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, Scale } from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { ShopifyRunSummary } from '../../../../components/warehouses/ShopifyRunSummary';
import { ShopifyScopesNotice } from '../../../../components/warehouses/ShopifyScopesNotice';
import { useNotice } from '../../../../hooks/useNotice';
import { reauthorizeShopify } from '../../../../features/ecommerces/ecommercesThunks';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import {
  setAlignmentFilters,
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import {
  activateStockSync,
  fetchStockPreview,
  fetchStockSync,
  fetchStockSyncChannels,
  startStockPreview,
} from '../../../../features/stockSync/stockSyncThunks';
import {
  selectAlignmentCounts,
  selectAlignmentPageRows,
  selectFilteredAlignmentRows,
  selectIsRunActive,
  selectPreviewState,
  selectRunOf,
  selectUsableChannels,
} from '../../../../features/stockSync/stockSyncSelectors';
import {
  ALIGNMENT_FILTER,
  RUN_KIND,
  SHOPIFY_ALIGNMENT_PATH,
  STOCK_SYNC_WATCHER,
} from '../../../../features/stockSync/stockSyncConstants';
import {
  PREVIEW_STATE,
  alignmentFilterOptions,
  alignmentPublishText,
  alignmentScopesProblemOf,
  alignmentStateText,
  alignmentStateTone,
  applyBlocker,
  applyConfirmMessage,
  canSyncStock,
  formatSyncDate,
  formatSyncTime,
  isAlignmentRowPublished,
  pickDefaultChannelId,
  previewSummaryLines,
  previewTokenOf,
  scopesProblemOf,
  shopDomain,
  stockCompareBlocker,
  withPeriod,
} from '../../../../features/stockSync/stockSyncUtils';
import { selectCurrentWarehouse } from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';
const BOX = 'rounded-2xl border p-4 text-sm space-y-3';
const WARNING_BOX = `${BOX} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300`;
const PLAIN_BOX = `${BOX} border-border bg-white text-ninja-dark dark:bg-[#1a1a1a] dark:text-white`;

const STATE_BADGE = {
  [PREVIEW_STATE.RUNNING]: { tone: 'info', label: 'Comparando' },
  [PREVIEW_STATE.READY]: { tone: 'success', label: 'Lista para confirmar' },
  [PREVIEW_STATE.EXPIRED]: { tone: 'warning', label: 'Vencida' },
  [PREVIEW_STATE.APPLIED]: { tone: 'neutral', label: 'Activada' },
  [PREVIEW_STATE.FAILED]: { tone: 'danger', label: 'Falló' },
};

/**
 * `/warehouse/inventory/shopify/sync?store=<id>`: the last comparison of a
 * Shopify store with Ninja, a row per linked product: what the store has, what
 * Ninja will publish, and whether the store goes up, down or stays. Ninja's
 * number rules the store: confirming turns publishing on and publishes every
 * product, once and within the hour; a new comparison voids the one before.
 * The store comes in the address; without it, the page picks it as the
 * dialogs do. The store is the seller's own: when it lacks the permissions,
 * the seller authorizes Ninja again from here and comes back to this page.
 */
export const InventoryShopifySync = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeParam = Number(searchParams.get('store')) || null;

  const warehouse = useSelector(selectCurrentWarehouse);
  const channels = useSelector(selectUsableChannels);
  const { channelsLoaded, selectedId, detail, detailError, alignment } = useSelector((state) => state.stockSync);
  const { preview, filters } = alignment;
  const previewState = useSelector(selectPreviewState);
  const pageRows = useSelector(selectAlignmentPageRows);
  const filteredRows = useSelector(selectFilteredAlignmentRows);
  const counts = useSelector(selectAlignmentCounts);
  const publishReport = useSelector(selectRunOf(RUN_KIND.PUBLISH));
  const publishing = useSelector(selectIsRunActive(RUN_KIND.PUBLISH));
  const activating = useSelector(selectIsRunning(activateStockSync));
  const reauthorizing = useSelector(selectIsRunning(reauthorizeShopify));

  const [searchInput, setSearchInput] = useState(filters.search);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [reauthorizeError, setReauthorizeError] = useState(null);
  const setNotice = useNotice('shopify-alignment-notice');

  const syncs = canSyncStock(detail);

  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.ALIGNMENT));
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.ALIGNMENT));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStockSyncChannels({ warehouseId: warehouse?.id ?? null }));
  }, [dispatch, warehouse?.id]);

  // The store in the address is the one in use; without one, the page picks it.
  useEffect(() => {
    if (storeParam) {
      dispatch(stockSyncChannelSelected(storeParam));
      return;
    }
    if (!channelsLoaded) return;
    const settingId = pickDefaultChannelId(channels, { selectedId });
    if (settingId) setSearchParams({ store: String(settingId) }, { replace: true });
  }, [dispatch, storeParam, channelsLoaded, channels, selectedId, setSearchParams]);

  useEffect(() => {
    if (!selectedId) return;
    dispatch(fetchStockSync({ settingId: selectedId }));
    dispatch(fetchStockPreview({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  // The rows are all here: the search only waits for the typing to stop.
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setAlignmentFilters({ search }));
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  const compare = async () => {
    const action = await dispatch(startStockPreview({ settingId: selectedId }));
    if (startStockPreview.rejected.match(action)) setNotice({ type: 'error', message: action.payload });
  };

  const activate = async () => {
    setConfirmError(null);
    const action = await dispatch(activateStockSync({ settingId: selectedId, token: previewTokenOf(preview) }));
    if (activateStockSync.fulfilled.match(action)) setConfirming(false);
    else setConfirmError(action.payload);
  };

  const handleReauthorize = async () => {
    setReauthorizeError(null);
    const action = await dispatch(
      reauthorizeShopify({ integrationId: selectedId, returnTo: `${SHOPIFY_ALIGNMENT_PATH}?store=${selectedId}` })
    );
    if (reauthorizeShopify.rejected.match(action)) setReauthorizeError(action.payload);
  };

  const scopesProblem = detail && scopesProblemOf(detail);
  const alignmentScopesProblem = detail && alignmentScopesProblemOf(detail);
  const blocker = detail ? applyBlocker({ detail, previewState }) : null;
  const compareBlocker = detail ? stockCompareBlocker(detail) : 'Elige la tienda';
  const hasRows = Boolean(preview?.result?.rows);
  const location = preview?.result?.location || detail?.location;
  const badge = STATE_BADGE[previewState];

  const columns = [
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div
          className="flex flex-col max-w-[240px]"
          data-testid="shopify-alignment-row"
          data-listing-id={row.listing_id}
          data-state={row.state}
        >
          {row.product_id ? (
            <Link
              to={`${INVENTORY_PATH}/${row.product_id}`}
              className="font-bold font-mono text-ninja-dark dark:text-white hover:text-ninja-green"
              title="Ver los movimientos de este producto"
            >
              {row.sku}
            </Link>
          ) : (
            <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku}</span>
          )}
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.title}>
            {row.title}
          </span>
        </div>
      ),
    },
    {
      header: 'TIENDA',
      cell: (row) => <span className="tabular-nums">{row.store === null ? '—' : formatQuantity(row.store)}</span>,
    },
    {
      header: 'NINJA',
      cell: (row) => <span className="tabular-nums">{row.ninja === null ? '—' : formatQuantity(row.ninja)}</span>,
    },
    {
      header: 'AL CONFIRMAR',
      cell: (row) => (
        // The table's cells do not wrap: why a product stays out has to, or it runs under the next column.
        <div className="flex flex-col items-start gap-1 max-w-[320px]">
          <ToneBadge tone={alignmentStateTone(row)} testId="shopify-alignment-action">
            {alignmentStateText(row)}
          </ToneBadge>
          {!isAlignmentRowPublished(row) && (
            <span className="text-xs text-text-muted whitespace-normal">{row.state_label}</span>
          )}
        </div>
      ),
    },
    {
      // What the store has now, and what it takes from Ninja.
      header: 'PUBLICA',
      cell: (row) => (
        <span className="tabular-nums" title={row.state_label}>
          {alignmentPublishText(row)}
        </span>
      ),
    },
  ];

  const isFiltering = filters.search !== '' || filters.state !== ALIGNMENT_FILTER.ALL;
  const emptyMessage = isFiltering
    ? 'No hay productos que coincidan con estos filtros.'
    : 'La tienda no tiene productos vinculados: sincroniza los SKUs en Productos.';

  const recompareButton = (label) => (
    <PendingButton
      pendingOn={startStockPreview}
      variant="outline"
      icon={Scale}
      loadingLabel="Comparando…"
      disabled={Boolean(compareBlocker)}
      title={compareBlocker || undefined}
      onClick={compare}
      data-testid="shopify-alignment-recompare"
    >
      {label}
    </PendingButton>
  );

  return (
    <div data-testid="shopify-alignment-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Inventario" to={INVENTORY_PATH} current="Sincronizar stock" />

      <DetailHero
        testId="shopify-alignment-hero"
        media={<Scale size={36} strokeWidth={1.5} />}
        title="Sincronizar stock con Shopify"
        badges={
          badge && (
            <ToneBadge tone={badge.tone} testId="shopify-alignment-state">
              {badge.label}
            </ToneBadge>
          )
        }
        meta={
          detail && (
            <>
              <span>{detail.channel.company.name}</span>
              <span>{shopDomain(detail.channel.shop_url)}</span>
              <span>
                {detail.channel.warehouse.name}
                {location ? ` → «${location.name}»` : ' · sin ubicación en la tienda'}
              </span>
              {preview?.finished_at && previewState !== PREVIEW_STATE.FAILED && (
                <span>
                  Comparado {formatSyncDate(preview.finished_at)}
                  {previewState === PREVIEW_STATE.READY && ` · vence a las ${formatSyncTime(preview.result?.expires_at)}`}
                </span>
              )}
            </>
          )
        }
      />

      {detailError && (
        <ErrorState
          variant="inline"
          title="No se pudo leer la tienda"
          description={detailError}
          onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
        />
      )}

      {!selectedId && channelsLoaded && channels.length === 0 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-alignment-none">
          Ningún cliente con el WMS en esta bodega tiene su tienda Shopify conectada.
        </p>
      )}

      {detail && !syncs && (
        <p className={PLAIN_BOX} data-testid="shopify-alignment-forbidden">
          Activar la publicación lo hace quien guarda mercadería en esta bodega, con el WMS.
        </p>
      )}

      {detail && syncs && (
        <>
          <ShopifyScopesNotice
            problem={scopesProblem}
            companyName={detail.channel.company.name}
            onReauthorize={handleReauthorize}
            reauthorizing={reauthorizing}
            error={reauthorizeError}
          />
          {!scopesProblem && (
            <ShopifyScopesNotice
              problem={alignmentScopesProblem}
              companyName={detail.channel.company.name}
              onReauthorize={handleReauthorize}
              reauthorizing={reauthorizing}
              error={reauthorizeError}
              forActivation
            />
          )}

          {previewState === PREVIEW_STATE.NONE && !alignment.loading && (
            <div className={PLAIN_BOX} data-testid="shopify-alignment-empty">
              <p>
                Todavía no se compara el stock de esta tienda. La comparación solo lee: la tienda no cambia hasta que
                confirmes.
              </p>
              {recompareButton('Comparar con la tienda')}
            </div>
          )}

          {previewState === PREVIEW_STATE.RUNNING && (
            <p className={clsx(PLAIN_BOX, 'flex items-center gap-2')} data-testid="shopify-alignment-running">
              <Loader2 size={18} aria-hidden="true" className="shrink-0 animate-spin text-info" />
              Comparando con la tienda… La tabla aparece sola cuando termine.
            </p>
          )}

          {previewState === PREVIEW_STATE.FAILED && (
            <ErrorState
              variant="inline"
              title="La comparación con la tienda falló"
              description={preview.error}
              onRetry={compare}
            />
          )}

          {previewState === PREVIEW_STATE.EXPIRED && (
            <div className={WARNING_BOX} data-testid="shopify-alignment-expired">
              <p className="flex items-start gap-2 font-semibold">
                <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
                La comparación venció a las {withPeriod(formatSyncTime(preview.result?.expires_at))} Compara de nuevo
                para confirmar: la tienda pudo cambiar desde entonces.
              </p>
              {recompareButton('Comparar de nuevo')}
            </div>
          )}

          {(previewState === PREVIEW_STATE.APPLIED || publishing) && (
            <div className="space-y-3" data-testid="shopify-alignment-applied">
              {previewState === PREVIEW_STATE.APPLIED && (
                <p className="flex items-center gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
                  <CheckCircle2 size={18} aria-hidden="true" className="shrink-0 text-ninja-green" />
                  Esta comparación ya se confirmó · {formatSyncDate(preview.applied_at)}: Ninja publica el stock en la
                  tienda.
                </p>
              )}
              <ShopifyRunSummary kind={RUN_KIND.PUBLISH} report={publishReport} />
              {previewState === PREVIEW_STATE.APPLIED && recompareButton('Comparar de nuevo')}
            </div>
          )}

          {hasRows && (
            <>
              <p className="text-sm text-ninja-dark dark:text-white" data-testid="shopify-alignment-summary">
                {previewSummaryLines(preview.result.summary)
                  .map((line) => line.text)
                  .join(' · ')}
              </p>

              <CountChips
                options={alignmentFilterOptions(counts, filters.state)}
                value={filters.state}
                onChange={(state) => dispatch(setAlignmentFilters({ state }))}
                allValue={ALIGNMENT_FILTER.ALL}
                allLabel="Todos"
                total={counts.all}
                testId="shopify-alignment-filter"
              />

              <FilterBar variant="bare" testId="shopify-alignment-filters">
                <SearchInput
                  variant="bare"
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder="Buscar por SKU o nombre..."
                  testId="shopify-alignment-search"
                />
              </FilterBar>

              <DataTable
                columns={columns}
                data={pageRows}
                totalItems={filteredRows.length}
                currentPage={filters.page}
                itemsPerPage={filters.rowsPerPage}
                isLoading={alignment.loading}
                error={alignment.error}
                onRetry={() => dispatch(fetchStockPreview({ settingId: selectedId }))}
                itemLabel="productos"
                emptyMessage={emptyMessage}
                idAccessor="listing_id"
                onPageChange={(page) => dispatch(setAlignmentFilters({ page }))}
                onRowsPerPageChange={(rowsPerPage) => dispatch(setAlignmentFilters({ rowsPerPage }))}
              />

              {previewState === PREVIEW_STATE.READY && (
                <div
                  className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-2xl border border-border bg-white p-4 shadow-lg dark:bg-[#1a1a1a] md:flex-row md:items-center md:justify-between"
                  data-testid="shopify-alignment-footer"
                >
                  <p className="text-sm text-ninja-dark dark:text-white" data-testid="shopify-alignment-note">
                    Al confirmar, la tienda toma el número de Ninja y la publicación queda encendida. Vence a las{' '}
                    {withPeriod(formatSyncTime(preview.result?.expires_at))}
                  </p>
                  <div className="flex flex-col items-stretch gap-1 md:items-end">
                    <Button
                      variant="primary"
                      icon={Scale}
                      disabled={Boolean(blocker)}
                      title={blocker || undefined}
                      onClick={() => {
                        setConfirmError(null);
                        setConfirming(true);
                      }}
                      data-testid="shopify-alignment-apply"
                    >
                      Confirmar
                    </Button>
                    {blocker && (
                      <p className="text-xs text-text-muted md:text-right" data-testid="shopify-alignment-apply-blocker">
                        {blocker}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={activate}
        title="¿Activar la publicación en la tienda?"
        message={applyConfirmMessage({ summary: preview?.result?.summary, location })}
        variant="primary"
        confirmLabel="Confirmar"
        loadingLabel="Confirmando..."
        loading={activating}
        error={confirmError}
        testId="shopify-alignment-confirm"
      />
    </div>
  );
};
